import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function AboutPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  // SEO Configuration
  useSEO({
    title: 'About PAINTERRR - South Africa\'s Trusted Painter Directory | Our Story',
    description: 'Learn about PAINTERRR, South Africa\'s premier directory connecting homeowners with professional, certified painters. Discover our mission to simplify finding quality painting services across Cape Town and beyond.',
    keywords: 'about PAINTERRR, painter directory South Africa, professional painters, painting services Cape Town, trusted painters',
    schema: generateWebPageSchema({
      name: 'About PAINTERRR - Our Story',
      description: 'Learn about PAINTERRR and our mission to connect South Africans with trusted professional painters.',
      url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`
    })
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Load Elfsight script
    const script = document.createElement('script');
    script.src = 'https://elfsightcdn.com/platform.js';
    script.async = true;
    document.body.appendChild(script);

    // Add Elfsight widget
    const widget = document.createElement('div');
    widget.className = 'elfsight-app-a8d09b4b-1f9c-47af-9c95-257e6e1aa6a7';
    widget.setAttribute('data-elfsight-app-lazy', '');
    document.body.appendChild(widget);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      if (widget.parentNode) {
        widget.parentNode.removeChild(widget);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'
      }`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-[#e9f638] rounded-lg flex items-center justify-center">
                <i className="ri-paint-brush-fill text-2xl text-black"></i>
              </div>
              <span className="text-2xl font-bold text-black">Painterrr</span>
            </Link>
            <div className="flex items-center space-x-6">
              <Link to="/#listings" className="text-gray-700 hover:text-[#e9f638] transition-colors whitespace-nowrap">
                Find Painters
              </Link>
              <Link to="/about" className="text-teal-600 font-medium whitespace-nowrap">
                About
              </Link>
              <Link to="/contact" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">
                Contact
              </Link>
              <Link to="/get-a-quote" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-[#d4e030] transition-colors whitespace-nowrap cursor-pointer">
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative bg-black text-white overflow-hidden mt-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/219342b587bed248b959e2e60e4985e5.jpg" 
            alt="About PAINTERRR" 
            className="w-full h-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>
        <div className="relative container mx-auto px-4 py-24 md:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              About <span className="text-[#e9f638]">Painterrr</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300">
              Connecting South Africans with trusted professional painters since day one
            </p>
          </div>
        </div>
      </div>

      {/* Our Story Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-center">
              Our <span className="text-[#e9f638]">Story</span>
            </h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                PAINTERRR was born from a simple observation: finding a reliable, professional painter in South Africa shouldn't be a stressful experience. Too many homeowners and businesses struggle to connect with quality painting professionals, often relying on word-of-mouth or taking chances with unverified contractors.
              </p>
              <p>
                We set out to change that. Our mission is to create a trusted platform where South Africans can easily discover, compare, and connect with certified painting professionals in their area. Every painter in our directory has been thoroughly vetted for quality, reliability, and customer satisfaction.
              </p>
              <p>
                What started as a small directory in Cape Town has grown into South Africa's premier painter connection platform, serving homeowners and businesses across the Western Cape and beyond. We're proud to have helped thousands of South Africans transform their spaces with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
            Our Mission &amp; <span className="text-[#e9f638]">Values</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#e9f638] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-fill text-3xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Trust &amp; Quality</h3>
              <p className="text-gray-600">
                We verify every painter in our directory to ensure they meet our high standards for professionalism, certification, and customer satisfaction.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#e9f638] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-fill text-3xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Customer First</h3>
              <p className="text-gray-600">
                Your satisfaction is our priority. We're committed to making your painter search simple, transparent, and stress-free from start to finish.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#e9f638] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-community-fill text-3xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Local Expertise</h3>
              <p className="text-gray-600">
                We connect you with painters who understand South African homes, climate conditions, and architectural styles unique to your area.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#e9f638] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-hand-heart-fill text-3xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Transparency</h3>
              <p className="text-gray-600">
                No hidden fees, no surprises. We believe in honest communication and clear pricing so you can make informed decisions.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#e9f638] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-lightbulb-flash-fill text-3xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Innovation</h3>
              <p className="text-gray-600">
                We continuously improve our platform to make finding and hiring painters easier, faster, and more reliable than ever before.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#e9f638] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-star-smile-fill text-3xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Excellence</h3>
              <p className="text-gray-600">
                We partner only with painters who share our commitment to exceptional workmanship and outstanding customer service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Help Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
            How We <span className="text-[#e9f638]">Help You</span>
          </h2>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#e9f638] rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-black">1</span>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-3">For Homeowners</h3>
                <p className="text-gray-600">
                  Find verified, professional painters in your area quickly and easily. Compare quotes, read reviews, and choose the perfect painter for your project with confidence.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#e9f638] rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-black">2</span>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-3">For Businesses</h3>
                <p className="text-gray-600">
                  Access commercial painting specialists who understand the unique needs of offices, retail spaces, and industrial properties. Get competitive quotes and professional service.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#e9f638] rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-black">3</span>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-3">Quality Assurance</h3>
                <p className="text-gray-600">
                  Every painter in our directory is vetted for certification, insurance, and customer satisfaction. We ensure you're connecting with legitimate professionals.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#e9f638] rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-black">4</span>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-3">Free Service</h3>
                <p className="text-gray-600">
                  Our platform is completely free for customers. Get multiple quotes, expert advice, and connect with painters at no cost to you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#e9f638]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-black mb-2">45+</div>
              <div className="text-lg text-gray-800">Verified Painters</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-black mb-2">1000+</div>
              <div className="text-lg text-gray-800">Projects Completed</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-black mb-2">98%</div>
              <div className="text-lg text-gray-800">Customer Satisfaction</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-black mb-2">50+</div>
              <div className="text-lg text-gray-800">Service Areas</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 text-center">
            Our <span className="text-[#e9f638]">Commitment</span>
          </h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              At PAINTERRR, we're more than just a directory. We're your partner in transforming your space. Our team works tirelessly to ensure every painter in our network meets our rigorous standards for quality, professionalism, and customer service.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              We understand that inviting someone into your home or business is a big decision. That's why we take the vetting process seriously, verifying credentials, checking references, and ensuring every painter has the proper insurance and certifications.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Whether you're refreshing a single room or undertaking a complete exterior renovation, we're here to connect you with the right professional for the job. Your satisfaction is our success.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied South Africans who found their perfect painter through PAINTERRR.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/get-a-quote"
              className="inline-block bg-[#e9f638] text-black px-12 py-4 rounded-lg font-bold text-lg hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Get Free Quotes
            </Link>
            <Link 
              to="/#listings"
              className="inline-block bg-black text-white px-12 py-4 rounded-lg font-bold text-lg hover:bg-gray-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Browse Painters
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/get-a-quote" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Get a Quote</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Cape Town</li>
                <li>Table View</li>
                <li>Durbanville</li>
                <li>Constantia</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by leaderr
              </a>
              <span>|</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
